import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import OAuthInput from '../OAuthFlow/shared/OAuthInput';
import OAuthButton from '../OAuthFlow/shared/OAuthButton';

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  text-align: center;
  margin-bottom: 8px;
  line-height: 32px;
`;

const Subtext = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.textSecondary};
  text-align: center;
  line-height: 24px;
  margin-bottom: 24px;
`;

const Note = styled.p`
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  line-height: 18px;
  text-align: center;
  margin-top: 16px;
`;

// Collects the user's Rivian credentials and posts them to the backend, which
// signs in to Rivian server-side. `otp_required` → advance to the code step;
// `complete` → the server hands back the app redirect_uri.
export default function PasswordStep({ email, setEmail, headers, onOtpRequired, onComplete }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = useCallback(async function (e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@') || !password) {
      setError('Enter your Rivian email and password.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/oauth/rivian/login', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email: trimmed, password: password }),
      });
      const data = await response.json();

      if (response.ok && data.status === 'otp_required') {
        onOtpRequired({ targetChannel: data.target_channel, channels: data.channels || [] });
        return;
      }
      if (response.ok && data.status === 'complete') {
        onComplete(data.redirect_to);
        return;
      }
      setError(data.message || 'Sign-in did not complete. Please try again.');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, headers, onOtpRequired, onComplete]);

  return (
    <form onSubmit={handleSubmit}>
      <Heading>Sign in with Rivian</Heading>
      <Subtext>Enter your Rivian account credentials to connect your vehicle.</Subtext>

      <OAuthInput
        label="Rivian account email"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(null); }}
        autoFocus
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      <OAuthInput
        label="Rivian account password"
        type="password"
        value={password}
        onChange={(e) => { setPassword(e.target.value); setError(null); }}
        error={error}
        autoComplete="current-password"
      />

      <OAuthButton type="submit" loading={loading} disabled={!email.trim() || !password}>
        Sign In
      </OAuthButton>

      <Note>
        Your credentials are sent securely to Rivian, used once to connect, and
        never stored on your device or our servers.
      </Note>
    </form>
  );
}
